import { api } from '@/shared/services/api';

export interface InvoiceLine {
  id?: string;
  productId?: string;
  description: string;
  quantity: number;
  unitPrice: number;
  vatRate: number;
  discount: number;
  discountType: string;
  lineTotal: number;
  vatAmount: number;
  /** True = linie cu valori exacte din e-Factura XML; backend NU recalculează la save.
      Frontend clears la primul edit pe qty/unitPrice/vatRate/discount. */
  preserveAmounts?: boolean;
  sortOrder: number;
  /** Câmpuri opționale legate de produs / linie — anterior accesate via `as any`. */
  stockManaged?: boolean;
  currentStock?: number | null;
  unit?: string;
  code?: string;
  vatType?: string;
  vatMention?: string | null;
  discountMode?: 'percent' | 'fixed';
  /** Populat când backend include relation product (ex /invoices/:id) */
  product?: { id?: string; code?: string | null; name?: string; unit?: string; isService?: boolean } | null;
}

export interface Invoice {
  id: string;
  tenantId: string;
  clientId: string;
  seriesId: string;
  type: string;
  series: string;
  number: number;
  issueDate: string;
  dueDate?: string;
  currency: string;
  exchangeRate?: number;
  direction?: 'outgoing' | 'incoming';
  status: string;
  paymentStatus: string;
  paidAmount: number;
  subtotal: number;
  vatTotal: number;
  total: number;
  notes?: string;
  deliveryAddress?: string;
  deliveryContact?: string;
  deliveryPhone?: string;
  shareToken?: string;
  emailSentAt?: string | null;
  firstViewedAt?: string | null;
  client?: { id: string; name: string; cui?: string; email?: string; phone?: string };
  lines: InvoiceLine[];
  createdAt: string;
  /** Câmpuri opționale care pot lipsi din răspunsuri parțiale — anterior accesate via `as any`. */
  updatedAt?: string;
  /** Optimistic concurrency: incrementat la fiecare update. Frontend îl trimite înapoi la save. */
  version?: number;
  /** Sablon PDF persistat per document (override fata de tenant default). null/undefined = foloseste tenant default. */
  pdfTemplateId?: string | null;
  internalNotes?: string;
  ratePolicy?: 'previous_day' | 'same_day';
  incoterms?: string;
  incotermsLocation?: string;
  intocmitDe?: string;
  intocmitCnp?: string;
  hidePrices?: boolean;
  // Setări de afișare/rotunjire preț, persistate per-document (null = legacy → default firmă).
  pricesIncludeVat?: boolean;
  priceDecimals?: number;
  vatRoundingMode?: 'line' | 'category';
  mentiuni?: string;
  delegat?: string;
  delegatBuletin?: string;
  delegatAuto?: string;
  efacturaSubmissions?: Array<{ status: string; sentAt?: string; validatedAt?: string }>;
  payments?: Array<{ id: string; amount: number; date: string; method: string; reference?: string | null }>;
}

export interface InvoiceSeries {
  id: string;
  tenantId: string;
  prefix: string;
  type: string;
  nextNumber: number;
  isActive: boolean;
  isDefault: boolean;
}

export interface InvoiceStats {
  draft: number;
  issued: number;
  paid: number;
  cancelled: number;
  // Σ cumulat (totalul valoric) și count pentru toate facturile valide din
  // perioada filtrată. Folosit de agg row în thead.
  totalIssuedSum: number;
  totalIssuedCount: number;
  collectedSum: number;
  // Distinct clienti per perioada filtrata.
  distinctClientCount: number;
  unpaidTotal: number;
  unpaidCount: number;
  overdueTotal: number;
  overdueCount: number;
  // Top categorie cheltuieli (doar pe direction=incoming) — folosit de KPI card
  // „Top categorie" pe /cheltuieli (înlocuiește vechiul „Anulate").
  topCategory?: { id: string; name: string; total: number; count: number } | null;
  // Count fără filtrul de perioadă, păstrând search/status/etc. Folosit de
  // SmartEmptyState pentru CTA „Caută în toate perioadele (N rezultate)".
  globalCount: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  meta?: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    // Sumele peste TOT setul filtrat (toate paginile), defalcate pe valută —
    // pentru footer-ul listei. Prezent doar în modul offset.
    totalsByCurrency?: Array<{ currency: string; sum: number; count: number }>;
  };
}

export interface CursorResponse<T> {
  data: T[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface BulkResult {
  succeeded: string[];
  failed: { id: string; reason: string }[];
}

export interface CreateInvoiceData {
  isReturn?: boolean;
  returnReason?: string;
  returnReasonText?: string;
  clientId?: string;
  seriesId?: string;
  direction?: 'outgoing' | 'incoming';
  expenseCategory?: string;
  expenseCategoryId?: string;
  attachmentUrl?: string;
  deliveryAddress?: string;
  deliveryAddressId?: string;
  deliveryContact?: string;
  deliveryPhone?: string;
  supplierSeries?: string;
  supplierNumber?: string;
  type: string;
  issueDate: string;
  dueDate?: string;
  currency?: string;
  exchangeRate?: number;
  notes?: string;
  internalNotes?: string;
  hidePrices?: boolean;
  pricesIncludeVat?: boolean;
  priceDecimals?: number;
  vatRoundingMode?: 'line' | 'category';
  ratePolicy?: 'previous_day' | 'same_day';
  incoterms?: string;
  incotermsLocation?: string;
  intocmitDe?: string;
  intocmitCnp?: string;
  delegat?: string;
  delegatBuletin?: string;
  delegatAuto?: string;
  mentiuni?: string;
  issuerId?: string;
  warehouseId?: string;
  destinationWarehouseId?: string;
  generateReceipt?: boolean;
  /** Default true on issue. Set to false to skip stock decrement. */
  descarcaStoc?: boolean;
  /** Doar proforma — soft reservation (Opțiunea B). Backend creează rânduri în
      stock_reservations la status='issued', care reduc disponibilul fără să atingă fizicul. */
  reservesStock?: boolean;
  /**
   * Optimistic concurrency: versiunea documentului citită la deschidere. Dacă
   * backend-ul observă că `version` curent în DB diferă, răspunde cu 409 Conflict.
   * Folosit pe update; pe create se ignoră.
   */
  versionExpected?: number;
  language?: string;
  status?: string;
  /** Optional pentru flow-uri incoming "fără linii" (bon fiscal, ordin plată,
   *  chitanță, alta) — backend acceptă lipsa și calculează din total/vatTotal. */
  lines?: {
    productId?: string;
    description: string;
    quantity: number;
    unitPrice: number;
    vatRate: number;
    vatType?: string;
    vatMention?: string | null;
    discount?: number;
    discountType?: string;
    sortOrder?: number;
  }[];

  // ── Câmpuri pentru cheltuieli incoming (modalul rapid — ExpenseQuickForm) ──
  /** Tip document incoming pentru cheltuieli simple. */
  documentType?: 'factura' | 'proforma' | 'bon_fiscal' | 'ordin_plata' | 'chitanta' | 'alta';
  /** Total brut introdus manual de user pentru flow-uri fără linii. */
  total?: number;
  /** TVA total (din total) pentru flow-uri fără linii. */
  vatTotal?: number;
  /** Metoda de plată pentru bon fiscal/chitanță/ordin plată. */
  paidMethod?: string;
  /** Referință la documentul de plată (nr. chitanță, OP etc.). */
  paidReference?: string;
  /** FK contul bancar (folosit la Card/Transfer pe cheltuieli). */
  bankAccountId?: string;
  /** Nume furnizor liber (text) când nu e selectat client din DB. */
  supplierNameText?: string;
}

/**
 * Date pentru înregistrarea unei plăți REALE odată cu crearea cheltuielii
 * (inline, fără să mai intri pe pagina de detalii). Trimis ca al 2-lea argument
 * la `onSubmit` din formularele de cheltuială (ExpenseQuickForm / InvoiceFormV2
 * pe `direction='incoming'`) și consumat de pagina `/cheltuieli`, care apelează
 * `POST /payments` după ce cheltuiala a fost creată. Spre deosebire de metadata
 * `paidMethod`/`paidReference` (care doar se persistă pe document), asta creează
 * efectiv un Payment și actualizează `paidAmount` + `paymentStatus`.
 */
export interface InlinePaymentRegistration {
  amount: number;
  date: string;
  method: string;
  reference?: string;
  bankAccountId?: string;
}

export const invoicesApi = {
  list: (params?: Record<string, string>) => {
    const query = params ? '?' + new URLSearchParams(params).toString() : '';
    return api.get<PaginatedResponse<Invoice>>('/invoices' + query);
  },
  listCursor: (params?: Record<string, string>) => {
    const query = params ? '?' + new URLSearchParams(params).toString() : '';
    return api.get<CursorResponse<Invoice>>('/invoices' + query);
  },
  get: (id: string) => api.get<Invoice>('/invoices/' + id),
  create: (data: CreateInvoiceData) => api.post<Invoice>('/invoices', data),
  update: (id: string, data: Partial<Omit<CreateInvoiceData, 'supplierNameText'>> & { paidAmount?: number; internalNotes?: string; supplierNameText?: string | null; refreshBuyerSnapshot?: boolean }) => api.patch<Invoice>('/invoices/' + id, data),
  remove: (id: string) => api.delete('/invoices/' + id),
  bulkDelete: (ids: string[]) => api.post<{ deleted: number; failed: number; errors: Array<{ id: string; reason: string }> }>('/invoices/bulk-delete', { ids }),
  bulkDuplicate: (ids: string[]) => api.post<{ duplicated: number; failed: number; errors: Array<{ id: string; reason: string }> }>('/invoices/bulk-duplicate', { ids }),
  duplicate: (id: string) => api.post<Invoice>('/invoices/' + id + '/duplicate', {}),
  setPdfTemplate: (id: string, templateId: string | null) =>
    api.patch<{ ok: true; pdfTemplateId: string | null }>('/invoices/' + id + '/pdf-template', { templateId }),
  storno: (id: string) => api.post<Invoice>('/invoices/' + id + '/storno', {}),
  cancel: (id: string) => api.post<Invoice>('/invoices/' + id + '/cancel', {}),
  bulkMarkPaid: (data: { ids: string[]; method?: string; date?: string; reference?: string }) =>
    api.post<BulkResult>('/invoices/bulk-mark-paid', data),
  stats: (params?: Record<string, string>) => {
    const qs = params && Object.keys(params).length ? '?' + new URLSearchParams(params).toString() : '';
    return api.get<InvoiceStats>('/invoices/stats' + qs);
  },
  downloadPdf: async (id: string, filename: string) => {
    const url = (process.env.NEXT_PUBLIC_API_URL || 'https://api.softfactura.ro') + '/api/v1/invoices/' + id + '/pdf';
    const response = await fetch(url, { credentials: 'include' });
    if (!response.ok) throw new Error('Eroare la descărcare PDF');
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  },
};

export const invoiceSeriesApi = {
  list: () => api.get<InvoiceSeries[]>('/invoice-series'),
  create: (data: { prefix: string; type: string; isDefault?: boolean }) =>
    api.post<InvoiceSeries>('/invoice-series', data),
  update: (id: string, data: Partial<InvoiceSeries>) =>
    api.patch<InvoiceSeries>('/invoice-series/' + id, data),
  remove: (id: string) => api.delete('/invoice-series/' + id),
};
